import React, { useState, useEffect } from 'react';
import { Banknote, TrendingUp, Wallet, ShieldCheck, PieChart, Info, Download, Filter, Landmark } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../Config';

const KpiCard = ({ title, value, icon: Icon, color, subValue }) => (
  <div className="bg-white rounded-enterprise border border-slate-100 p-6 shadow-sm hover:shadow-md transition-all duration-300">
    <div className="flex items-center justify-between mb-4">
      <div className={`p-3 rounded-xl ${color} bg-opacity-10`}>
        <Icon className={`w-6 h-6 ${color.replace('bg-', 'text-')}`} />
      </div>
    </div>
    <p className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-1">{title}</p>
    <h3 className="text-2xl font-black text-slate-800 tracking-tight">{value}</h3>
    {subValue && <p className="text-xs font-semibold text-uni-green mt-2 flex items-center gap-1">
      <TrendingUp className="w-3 h-3" />
      {subValue}
    </p>}
  </div>
);

export default function PrestamoDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PY', {
      style: 'currency',
      currency: 'PYG',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/creditos/stats`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error("Error fetching credit stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-uni-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-premium-in p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Análisis de Cartera de Créditos</h1>
          <p className="text-slate-500 font-medium">Panel de control financiero y salud crediticia</p>
        </div>
        <div className="flex gap-2">
          <button className="enterprise-button-secondary py-2 px-4 flex items-center gap-2 text-sm">
            <Filter className="w-4 h-4" />
            Filtros
          </button>
          <button className="enterprise-button-primary py-2 px-4 flex items-center gap-2 text-sm shadow-lg shadow-uni-green/20">
            <Download className="w-4 h-4" />
            Exportar PDF
          </button>
        </div>
      </div>

      {/* KPI Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard 
          title="Cartera Total (Colocado)" 
          value={formatCurrency(stats?.montoTotalColocado)}
          icon={Landmark}
          color="bg-blue-500"
          subValue="Histórico acumulado"
        />
        <KpiCard 
          title="Saldo de Capital Vivo" 
          value={formatCurrency(stats?.saldoCapitalPendiente)}
          icon={Wallet}
          color="bg-uni-green"
          subValue="Exposición actual"
        />
        <KpiCard 
          title="Préstamos Activos" 
          value={stats?.cantidadPrestamosActivos || 0}
          icon={ShieldCheck}
          color="bg-indigo-500"
          subValue="Operaciones vigentes"
        />
        <KpiCard 
          title="Ticket Promedio" 
          value={formatCurrency((stats?.montoTotalColocado || 0) / (stats?.cantidadPrestamosActivos || 1))}
          icon={Banknote}
          color="bg-amber-500"
          subValue="Por operación"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Loan type distribution */}
        <div className="bg-white p-7 rounded-enterprise border border-slate-100 shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-uni-green/10 rounded-lg">
                <PieChart className="w-5 h-5 text-uni-green" />
              </div>
              <h3 className="font-black text-slate-800 uppercase tracking-widest text-sm">Distribución por Línea</h3>
            </div>
            <Info className="w-4 h-4 text-slate-300" />
          </div>
          
          <div className="space-y-6">
            {Object.entries(stats?.porLinea || {}).map(([linea, cantidad]) => (
              <div key={linea} className="group">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-bold text-slate-600 group-hover:text-uni-green transition-colors">{linea}</span>
                  <span className="text-sm font-black text-slate-800">{cantidad}</span>
                </div>
                <div className="w-full bg-slate-50 rounded-full h-2.5 overflow-hidden">
                  <div 
                    className="bg-uni-green h-full rounded-full transition-all duration-1000 shadow-sm"
                    style={{ width: `${(cantidad / (stats?.cantidadPrestamosActivos || 1)) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Portfolio Status */}
        <div className="bg-white p-7 rounded-enterprise border border-slate-100 shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/10 rounded-lg">
                <ShieldCheck className="w-5 h-5 text-indigo-500" />
              </div>
              <h3 className="font-black text-slate-800 uppercase tracking-widest text-sm">Estado de Cartera</h3>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(stats?.porEstado || {}).map(([estado, cantidad]) => (
              <div key={estado} className="p-5 rounded-2xl bg-slate-50 border border-slate-100 hover:border-uni-green/30 transition-all cursor-default group">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">{estado}</p>
                <div className="flex items-end justify-between">
                  <span className="text-2xl font-black text-slate-800 group-hover:text-uni-green transition-colors">{cantidad}</span>
                  <div className="text-[10px] font-bold text-slate-400 bg-white px-2 py-1 rounded-md border border-slate-100">
                    {((cantidad / (stats?.cantidadPrestamosActivos || 1)) * 100).toFixed(1)}%
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-4 bg-slate-50 rounded-xl border border-dashed border-slate-200">
            <p className="text-xs text-slate-400 text-center leading-relaxed font-medium">
              Los datos reflejan la situación financiera en tiempo real basada en desembolsos y pagos registrados.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
